/**
 * Hooks personnalisés pour le système de monitoring
 * Intégration avec RTK Query pour l'API réelle
 * @author GitHub Copilot
 * @date 2025-07-11
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { 
  useGetGlobalMetricsQuery, 
  useGetAgentMetricsQuery 
} from '../store/slices/apiSlice';
import {
  setAutoRefresh,
  setRefreshInterval,
  updateFilters,
  updatePreferences,
  addAlert,
  markAlertAsRead,
  clearError,
  logEvent,
  selectDashboardState,
  selectFilters,
  selectPreferences,
  selectAlerts,
  selectUnreadAlerts
} from '../store/slices/monitoringSlice';
import type {
  MonitoringFilters,
  MonitoringPreferences,
  MonitoringAlert,
  MonitoringEvent,
  MetricsResponse,
  MetricsAgent
} from '../types/monitoring.types';

/**
 * Hook principal pour la gestion du monitoring avec RTK Query
 */
export const useMonitoring = (options?: { 
  pollingInterval?: number; 
  skip?: boolean;
  refetchOnFocus?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const dashboardState = useAppSelector(selectDashboardState);
  
  // Utilisation de RTK Query pour récupérer les données
  const {
    data: metrics,
    isLoading,
    error,
    refetch,
    isError,
    isSuccess,
    isFetching
  } = useGetGlobalMetricsQuery(undefined, {
    pollingInterval: options?.pollingInterval || (dashboardState.autoRefresh ? dashboardState.refreshInterval * 1000 : 0),
    skip: options?.skip || false,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: options?.refetchOnFocus !== false,
  });

  // Actions de base
  const refreshMetrics = useCallback(() => {
    refetch();
  }, [refetch]);

  const clearErrorMessage = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Gestion de l'auto-refresh
  const toggleAutoRefresh = useCallback((enabled: boolean) => {
    dispatch(setAutoRefresh(enabled));
  }, [dispatch]);

  const setPollingInterval = useCallback((interval: number) => {
    dispatch(setRefreshInterval(interval));
  }, [dispatch]);

  // Gestion des événements
  const logMonitoringEvent = useCallback((event: Omit<MonitoringEvent, 'id' | 'timestamp'>) => {
    dispatch(logEvent(event));
  }, [dispatch]);

  // Données dérivées avec valeurs par défaut
  const summary = useMemo(() => metrics?.summary || {
    total_conversations: 0,
    total_cost_usd: 0,
    active_agents: 0
  }, [metrics]);

  const agents = useMemo(() => metrics?.agents || [], [metrics]);
  
  const performance = useMemo(() => metrics?.performance || {
    api_avg_latency_ms: 0,
    error_rate_percent: 0
  }, [metrics]);

  const lastUpdated = useMemo(() => metrics?.last_updated ? new Date(metrics.last_updated) : null, [metrics]);

  // État formaté pour l'UI
  const formattedMetrics = useMemo(() => ({
    summary: {
      ...summary,
      total_cost_formatted: `$${summary.total_cost_usd.toFixed(2)}`,
      conversations_growth: '+12%', // TODO: calculer la croissance réelle
      cost_growth: '+8%'
    },
    agents: agents.map(agent => ({
      ...agent,
      cost_formatted: `$${agent.cost_usd.toFixed(2)}`,
      response_time_formatted: `${agent.avg_response_time_ms}ms`,
      status: agent.avg_response_time_ms < 500 ? 'good' : agent.avg_response_time_ms < 1000 ? 'warning' : 'critical'
    })),
    performance: {
      ...performance,
      latency_formatted: `${performance.api_avg_latency_ms}ms`,
      error_rate_formatted: `${performance.error_rate_percent.toFixed(1)}%`,
      status: performance.error_rate_percent < 1 ? 'good' : performance.error_rate_percent < 5 ? 'warning' : 'critical'
    }
  }), [summary, agents, performance]);

  return {
    // Données
    metrics,
    summary,
    agents,
    performance,
    formattedMetrics,
    lastUpdated,
    
    // États RTK Query
    isLoading,
    isFetching,
    isError,
    isSuccess,
    error,
    
    // Actions
    refreshMetrics,
    clearErrorMessage,
    toggleAutoRefresh,
    setPollingInterval,
    logMonitoringEvent,
    
    // Configuration
    autoRefresh: dashboardState.autoRefresh,
    refreshInterval: dashboardState.refreshInterval,
  };
};

/**
 * Hook pour récupérer les métriques d'un agent spécifique
 */
export const useAgentMetrics = (agentId: string, options?: {
  pollingInterval?: number;
  skip?: boolean;
}) => {
  const {
    data: agentMetrics,
    isLoading,
    error,
    refetch,
    isError,
    isSuccess
  } = useGetAgentMetricsQuery(agentId, {
    pollingInterval: options?.pollingInterval || 30000,
    skip: options?.skip || !agentId,
    refetchOnMountOrArgChange: true,
  });

  const formattedMetrics = useMemo(() => {
    if (!agentMetrics) return null;
    
    return {
      ...agentMetrics,
      cost_formatted: `$${agentMetrics.cost_usd.toFixed(2)}`,
      response_time_formatted: `${agentMetrics.avg_response_time_ms}ms`,
      status: agentMetrics.avg_response_time_ms < 500 ? 'good' : 
               agentMetrics.avg_response_time_ms < 1000 ? 'warning' : 'critical'
    };
  }, [agentMetrics]);

  return {
    agentMetrics,
    formattedMetrics,
    isLoading,
    isError,
    isSuccess,
    error,
    refetch,
  };
};

/**
 * Hook pour la gestion des filtres de monitoring
 */
export const useMonitoringFilters = () => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectFilters);

  const updateFilter = useCallback((filterUpdates: Partial<MonitoringFilters>) => {
    dispatch(updateFilters(filterUpdates));
  }, [dispatch]);

  const resetFilters = useCallback(() => {
    dispatch(updateFilters({
      dateRange: 'today',
      selectedAgents: [],
      showInactive: false
    }));
  }, [dispatch]);

  return {
    filters,
    updateFilter,
    resetFilters,
  };
};

/**
 * Hook pour la gestion des préférences utilisateur
 */
export const useMonitoringPreferences = () => {
  const dispatch = useAppDispatch();
  const preferences = useAppSelector(selectPreferences);

  const updatePreference = useCallback((preferenceUpdates: Partial<MonitoringPreferences>) => {
    dispatch(updatePreferences(preferenceUpdates));
  }, [dispatch]);

  return {
    preferences,
    updatePreference,
  };
};

/**
 * Hook pour la gestion des alertes de monitoring
 */
export const useMonitoringAlerts = () => {
  const dispatch = useAppDispatch();
  const alerts = useAppSelector(selectAlerts);
  const unreadAlerts = useAppSelector(selectUnreadAlerts);

  const addMonitoringAlert = useCallback((alert: Omit<MonitoringAlert, 'id' | 'timestamp' | 'isRead'>) => {
    dispatch(addAlert(alert));
  }, [dispatch]);

  const markAsRead = useCallback((alertId: string) => {
    dispatch(markAlertAsRead(alertId));
  }, [dispatch]);

  const markAllAsRead = useCallback(() => {
    alerts.forEach(alert => {
      if (!alert.isRead) {
        dispatch(markAlertAsRead(alert.id));
      }
    });
  }, [alerts, dispatch]);

  return {
    alerts,
    unreadAlerts,
    unreadCount: unreadAlerts.length,
    addAlert: addMonitoringAlert,
    markAsRead,
    markAllAsRead,
  };
};

/**
 * Hook pour vérifier les seuils et générer des alertes automatiques
 */
export const useMonitoringThresholds = () => {
  const { metrics, formattedMetrics } = useMonitoring();
  const { addAlert } = useMonitoringAlerts();

  useEffect(() => {
    if (!metrics) return;

    // Vérification du taux d'erreur
    if (metrics.performance.error_rate_percent > 5) {
      addAlert({
        type: 'error',
        title: 'Taux d\'erreur élevé',
        message: `Le taux d'erreur a atteint ${metrics.performance.error_rate_percent.toFixed(1)}%`,
        actionRequired: true
      });
    }

    // Vérification de la latence API
    if (metrics.performance.api_avg_latency_ms > 1000) {
      addAlert({
        type: 'warning',
        title: 'Latence API élevée',
        message: `La latence moyenne est de ${metrics.performance.api_avg_latency_ms}ms`,
        actionRequired: false
      });
    }

    // Vérification des agents avec temps de réponse élevé
    metrics.agents.forEach(agent => {
      if (agent.avg_response_time_ms > 2000) {
        addAlert({
          type: 'warning',
          title: 'Temps de réponse agent élevé',
          message: `${agent.agent_name}: ${agent.avg_response_time_ms}ms`,
          actionRequired: false
        });
      }
    });
  }, [metrics, addAlert]);

  return {
    thresholds: {
      errorRate: { warning: 1, critical: 5 },
      latency: { warning: 500, critical: 1000 },
      responseTime: { warning: 1000, critical: 2000 }
    }
  };
};

/**
 * Hook pour exporter les données de monitoring
 */
export const useMonitoringExport = () => {
  const { metrics } = useMonitoring();

  const exportToCSV = useCallback(() => {
    if (!metrics) return;

    const csvData = [
      ['Agent ID', 'Agent Name', 'Conversations', 'Cost (USD)', 'Avg Response Time (ms)'],
      ...metrics.agents.map(agent => [
        agent.agent_id,
        agent.agent_name,
        agent.conversations_count.toString(),
        agent.cost_usd.toString(),
        agent.avg_response_time_ms.toString()
      ])
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `monitoring-metrics-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    URL.revokeObjectURL(url);
  }, [metrics]);

  const exportToJSON = useCallback(() => {
    if (!metrics) return;

    const jsonContent = JSON.stringify(metrics, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `monitoring-metrics-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    
    URL.revokeObjectURL(url);
  }, [metrics]);

  return {
    exportToCSV,
    exportToJSON,
    canExport: !!metrics,
  };
};
